import { DestroyRef, ErrorHandler, Injectable, Injector, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { GraphQLClient } from '../graphql/client';
import { GraphQLExecutionError } from '@branduals/graphql-errors';
import { MembershipService } from './membership.service';
import { SystemAccessService } from './system-access.service';
import { CacheResetService } from './cache-reset.service';

/**
 * The mid-session catch for a member whose brand access an admin paused while they were
 * working: consumes the GraphQL client's access-denial notifications, re-checks the
 * membership standing against the server, and — when the pause is real — walls the
 * member on /access-paused instead of letting fifteen surfaces each show a lock face
 * with a doomed Retry.
 *
 * Constructed at app boot (the shell holds a reference); acts only when the LAST-KNOWN
 * standing was 'active' — anonymous surfaces, true non-members and an already-paused
 * member have their own corridors (guards + the wall itself).
 */
@Injectable({ providedIn: 'root' })
export class PausedRedirectService {
  private readonly membership = inject(MembershipService);
  private readonly systemAccess = inject(SystemAccessService);
  private readonly cacheReset = inject(CacheResetService);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);

  /**
   * True from the moment a denial event arrives until its re-check resolves — the app
   * error handler suppresses the access-denial toast inside this window so the member
   * never gets scolded on top of the very page that explains their state.
   */
  private readonly _suppressionOpen = signal(false);
  readonly suppressionOpen = this._suppressionOpen.asReadonly();

  /**
   * The FIRST denial's error object, held through the re-check. A same-class burst
   * re-surfaces as exactly ONE toast — the held first error stands for the class; the
   * siblings are the same refusal repeated, not distinct information.
   */
  private heldError: GraphQLExecutionError | null = null;
  private checking = false;

  constructor() {
    const graphql = inject(GraphQLClient);
    // Root singletons leak nothing in production, but undestroyed subscriptions bleed
    // across TestBed instances in the spec harness — tie to the injector's lifetime.
    graphql.accessDenied$
      .pipe(takeUntilDestroyed(inject(DestroyRef)))
      .subscribe(error => void this.onAccessDenied(error));
  }

  private async onAccessDenied(error: GraphQLExecutionError): Promise<void> {
    if (this.membership.membershipState().standing !== 'active') return;
    if (this.checking) return; // the first denial's re-check stands for the whole burst
    this.checking = true;
    this.heldError = error;
    this._suppressionOpen.set(true);

    try {
      // Writes the fresh standing THROUGH the service (cache-bypassing, joins any
      // in-flight fetch) — without the write, the guard's cached 'active' would bounce
      // this service's own redirect straight back. reload() resolves-on-failure by
      // contract (a failed fetch sets loadError and resolves); a rejecting reload here
      // would skip the window-closing arms below and latch the suppression open.
      await this.membership.reload();
    } finally {
      this.checking = false;
    }

    const state = this.membership.membershipState();
    if (state.loaded && state.standing === 'paused' && !this.systemAccess.isRoot()) {
      this._suppressionOpen.set(false);
      this.heldError = null;
      // The sticky privilege cache would keep re-admitting member routes post-catch.
      this.systemAccess.invalidateCache();
      // Release member realtime WITHOUT a cache reset — a full reset would wipe the very
      // standing the access-paused wall is about to read.
      this.cacheReset.releaseRealtimeSubscriptions();
      if (!this.router.url.startsWith('/access-paused')) {
        this.router.navigate(['/access-paused']);
      }
      return;
    }

    // Non-paused resolution (removed entirely, or a transient policy refusal): close the
    // window FIRST, then re-dispatch the held error through the app ErrorHandler — one
    // code→toast home, so a denial never silently vanishes for a non-paused user.
    this._suppressionOpen.set(false);
    const held = this.heldError;
    this.heldError = null;
    if (held) {
      this.injector.get(ErrorHandler).handleError(held);
    }
  }
}
